import { ReportType } from '@anihis/core/data-access';

export interface FormDefinition {
  id?: string;
  category?: string;
  name: string;
  isBigHeight?: boolean;
  inputs?: Array<{
    formControlName: string;
    require?: boolean;
    type?: 'input' | 'textbox' | 'date' | 'checkbox' | 'select';
    hint?: string;
  }>;
  groups?: Array<{
    groupName: string;
    inputs: Array<{
      formControlName: string;
      require?: boolean;
      type?: 'input' | 'textbox' | 'date' | 'checkbox' | 'select';
      hint?: string;
    }>;
  }>;
  type: ReportType;
}

export const forms: FormDefinition[] = [
  {
    id: 'dental-record',
    name: 'Dental Record',
    category: 'ExternalDocuments',
    inputs: [
      { formControlName: 'plaqueGD' },
      { formControlName: 'plaqueGL' },
      { formControlName: 'plaqueDD' },
      { formControlName: 'plaqueDL' },
      { formControlName: 'calculusGD' },
      { formControlName: 'calculusGL' },
      { formControlName: 'calculusDD' },
      { formControlName: 'calculusDL' },
      { formControlName: 'gingivitisGD' },
      { formControlName: 'gingivitisGL' },
      { formControlName: 'gingivitisDD' },
      { formControlName: 'gingivitisDL' },
      { formControlName: 'periodontitisGD' },
      { formControlName: 'periodontitisGL' },
      { formControlName: 'periodontitisDD' },
      { formControlName: 'periodontitisDL' },
      { formControlName: 'comments' },
      { formControlName: 'extractedTooth' },
      { formControlName: 'repairTooth' },
      { formControlName: 'cleaningTooth' },
      { formControlName: 'prosthesis' },
    ],
    type: ReportType.Unknown,
  },
  {
    id: 'result-trio-dog',
    name: 'TRIO Test Results (Dog)',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'cpv', type: 'textbox' },
      { formControlName: 'ccv', type: 'textbox' },
      { formControlName: 'giardia', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultTrioDog,
  },

  {
    id: 'result-trio-cat',
    name: 'TRIO Test Results (Cat)',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'fiv', type: 'textbox' },
      { formControlName: 'felv', type: 'textbox' },
      { formControlName: 'fcov', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultTrioCat,
  },
  {
    id: 'cpv-ccv-finding',
    name: 'CPV and CCV Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'cpv', type: 'textbox' },
      { formControlName: 'ccv', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.CpvCcvFinding,
  },

  {
    id: 'ultrasound-finding',
    name: 'Ultrasound Finding',
    category: 'Findings',
    isBigHeight: true,
    type: ReportType.UltrasoundFinding,
  },
  {
    id: 'urine-finding',
    name: 'Urine Finding',
    category: 'Findings',
    isBigHeight: true,
    type: ReportType.UrineFinding,
  },
  {
    id: 'spermogram-finding',
    name: 'Spermogram Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'method', type: 'textbox' },
      { formControlName: 'volume', type: 'textbox' },
      { formControlName: 'color', type: 'textbox' },
      { formControlName: 'smell', type: 'textbox' },
      { formControlName: 'consistency', type: 'textbox' },
      { formControlName: 'bloodPresence', type: 'textbox' },
      { formControlName: 'otherImpurities', type: 'textbox' },
      { formControlName: 'progressiveMotility', type: 'textbox' },
      { formControlName: 'slowMotile', type: 'textbox' },
      { formControlName: 'dead', type: 'textbox' },
      { formControlName: 'agglutination', type: 'textbox' },
      { formControlName: 'opinion', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.SpermogramFinding,
  },
  {
    id: 'rtg-finding',
    name: 'RTG Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'previousAnalyses', type: 'textbox' },
      { formControlName: 'anamnesis', type: 'textbox' },
      { formControlName: 'clinicalFinding', type: 'textbox' },
      { formControlName: 'uzFinding', type: 'textbox' },
      { formControlName: 'rtgInterpretation', type: 'textbox' },
      { formControlName: 'rtgDiagnosis', type: 'textbox' },
      { formControlName: 'therapy', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.RtgFinding,
  },
  {
    id: 'radiology-finding',
    name: 'Radiology Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'patientHistory', type: 'textbox' },
      { formControlName: 'radiologyFinding', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.RadiologyFinding,
  },
  {
    id: 'orthopedic-finding',
    name: 'Orthopedic Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'finding', type: 'textbox' },
      { formControlName: 'opinion', type: 'textbox' },
      { formControlName: 'recommendation', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OrthopedicFinding,
  },
  {
    id: 'mri-finding',
    name: 'MRI Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'mriFinding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.MriFinding,
  },
  {
    id: 'leishmania-finding',
    name: 'Leishmania Test',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'leishmaniaTest', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.LeishmaniaFinding,
  },
  {
    id: 'cdv-finding',
    name: 'CDV Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'cdvTest', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.CdvFinding,
  },
  {
    id: 'baep-finding',
    name: 'BAEP Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'baepTest', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.BaepFinding,
  },
  {
    id: 'babesia-finding',
    name: 'Babesia Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'babesiaTest', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.BabesiaFinding,
  },
  {
    id: 'analysis-points',
    name: 'Punctate Fluid Analysis',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'specificGravity' },
      { formControlName: 'nitrites' },
      { formControlName: 'ph' },
      { formControlName: 'proteins' },
      { formControlName: 'glucose' },
      { formControlName: 'urobilinogen' },
      { formControlName: 'bilirubin' },
      { formControlName: 'ketones' },
      { formControlName: 'leukocytes' },
      { formControlName: 'bloodErythrocytes' },
      { formControlName: 'sediment', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.AnalysisPointsFinding,
  },
  {
    id: 'preoperativna-priprema-pacijenta',
    name: 'Preoperative Preparation',
    category: 'Instructions',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.PreoperativePreparation,
  },
  {
    id: 'nalaz-4d-l',
    name: '4D L Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'heartworm', type: 'textbox' },
      { formControlName: 'anaplasma', type: 'textbox' },
      { formControlName: 'ehrlichia', type: 'textbox' },
      { formControlName: 'leishmania', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.Nalaz4dL,
  },
  {
    id: 'contract-veterinary-services',
    name: 'Contract for Provision of Veterinary Services',
    category: 'Consent',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ContractVeterinaryServices,
  },
  {
    id: 'owner-statement-vaccination',
    name: 'Owner Statement Regarding Vaccination Against Infectious Diseases',
    category: 'Consent',
    isBigHeight: true,
    inputs: [
      { formControlName: 'numMales' },
      { formControlName: 'numFemales' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementVaccination,
  },
  {
    id: 'owner-statement-new-passport',
    name: 'Owner Statement Regarding Issuance of New Passport',
    category: 'Consent',
    isBigHeight: true,
    inputs: [
      { formControlName: 'currentPassportStatus', type: 'select' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementNewPassport,
  },
  {
    id: 'owner-statement-deregistration',
    name: 'Owner Statement Regarding Deregistration From Central Database',
    category: 'Consent',
    isBigHeight: true,
    inputs: [
      { formControlName: 'deregistrationReason', type: 'select' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementDeregistration,
  },
  {
    id: 'owner-statement-debt-installments',
    name: 'Statement of Debt Acceptance and Installment Payment',
    category: 'Consent',
    type: ReportType.OwnerStatementDebtInstallments,
  },
  {
    id: 'owner-statement-treatment-procedure',
    name: 'Consent for Treatment / Procedure',
    category: 'Consent',
    inputs: [
      { formControlName: 'anamnesis', type: 'textbox' },
      { formControlName: 'clinicalFinding', type: 'textbox' },
      { formControlName: 'therapy', type: 'textbox' },
      { formControlName: 'recommendation', type: 'textbox' },
      { formControlName: 'explainedItems', type: 'select' },
      { formControlName: 'proxyName', type: 'input' },
      { formControlName: 'proxyPhone', type: 'input' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementTreatmentProcedure,
  },
  {
    id: 'owner-statement-dental-treatment',
    name: 'Consent for Dental Treatment / Procedure',
    category: 'Consent',
    inputs: [
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'estimatedCostFrom', type: 'input' },
      { formControlName: 'estimatedCostTo', type: 'input' },
      { formControlName: 'additionalCostFrom', type: 'input' },
      { formControlName: 'additionalCostTo', type: 'input' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementDentalTreatment,
  },
  {
    id: 'owner-statement-inpatient-admission',
    name: 'Form for Admission to Inpatient Treatment - Inpatient',
    category: 'Consent',
    inputs: [
      { formControlName: 'admissionReason', type: 'textbox' },
      { formControlName: 'preventiveActions', type: 'textbox' },
      { formControlName: 'externalParasites', type: 'textbox' },
      { formControlName: 'internalParasites', type: 'textbox' },
      { formControlName: 'heartwormTreatment', type: 'textbox' },
      { formControlName: 'infectiousVaccines', type: 'textbox' },
      { formControlName: 'rabiesVaccine', type: 'textbox' },
      { formControlName: 'medicalHistory', type: 'textbox' },
      { formControlName: 'agreedPrepaidAmount', type: 'input' },
      { formControlName: 'agreedAdditionalAmount', type: 'input' },
      { formControlName: 'depositAmount', type: 'input' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementInpatientAdmission,
  },

  {
    id: 'prijemni-formular',
    name: 'Admission form',
    category: 'Consent',
    inputs: [
      { formControlName: 'initialNotes', type: 'textbox' },
      { formControlName: 'prevTreated', type: 'select' },
      { formControlName: 'prevVetNotes', type: 'textbox' },
      { formControlName: 'vaccineRabies', type: 'textbox' },
      { formControlName: 'vaccineInfectious', type: 'textbox' },
      { formControlName: 'externalParasites', type: 'textbox' },
      { formControlName: 'internalParasites', type: 'textbox' },
      { formControlName: 'heardFrom', type: 'select' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.PrijemniFormular,
  },
  {
    id: 'owner-statement-ownership-change',
    name: 'Owner Statement Regarding Change of Ownership',
    category: 'Consent',
    inputs: [
      { formControlName: 'animalName', type: 'input' },
      { formControlName: 'previousOwnerName', type: 'input' },
      { formControlName: 'newOwnerName', type: 'input' },
      { formControlName: 'newOwnerAddress', type: 'input' },
      { formControlName: 'newOwnerPhone', type: 'input' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnerStatementOwnershipChange,
  },
  {
    id: 'koproloski-finding',
    name: 'Coprological Finding',
    category: 'Findings',
    isBigHeight: true,
    inputs: [
      { formControlName: 'makroscopicFinding', type: 'textbox' },
      { formControlName: 'mikroscopicFinding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.KoproloskiFinding,
  },
  {
    id: 'ophthalmology-report',
    name: 'Ophthalmology Report',
    category: 'Findings',
    isBigHeight: true,
    type: ReportType.OphthalmologyReport,
  },
  {
    id: 'pregled-oka',
    name: 'Eye examination',
    category: 'Findings',
    isBigHeight: false,
    type: ReportType.Unknown,
  },
  {
    id: 'nalaz-neuroloski',
    name: 'Findings - Neurology',
    category: 'Findings',
    // downloadable PDF available in assets/document/Nalaz-Neuroloski.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'endoskopski-nalaz',
    name: 'Endoscopy Report',
    category: 'Findings',
    // downloadable PDF available in assets/document/Endoskopski-pregled-Gornji-digestivni-trakt.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'nalaz-echo-srca-macke',
    name: 'Findings - Cat heart echo',
    category: 'Findings',
    // downloadable PDF available in assets/document/Nalaz-Echo-srca-macke.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'nalaz-echo-srca-psa',
    name: 'Findings - Dog heart echo',
    category: 'Findings',
    // downloadable PDF available in assets/document/Nalaz-Echo-srca-psa.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'nalaz-ekg',
    name: 'ECG',
    category: 'Findings',
    // downloadable PDF available in assets/document/Nalaz-EKG.pdf.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'referral-for-histopathology',
    name: 'Referral for histopathology',
    category: 'ExternalDocuments',
    isBigHeight: true,
    type: ReportType.ReferralForHistopathology,
  },
  {
    id: 'request-for-examination',
    name: 'Request for examination',
    category: 'ExternalDocuments',
    type: ReportType.RequestForExamination,
  },
  {
    id: 'vetlab-anamnesticki',
    name: 'VetLab Anamnesis Request',
    category: 'ExternalDocuments',
    type: ReportType.VetLabAnamnesisRequest,
  },
  {
    id: 'zahtev-vetlab',
    name: 'VetLab request',
    category: 'ExternalDocuments',
    // This entry represents a downloadable Serbian-only PDF available in assets/document
    type: ReportType.Unknown,
  },
  {
    id: 'zahtev-seqomics',
    name: 'SeqoMics Request - Genetics',
    category: 'ExternalDocuments',
    // Downloadable Serbian-only SeqoMics genetics request PDF
    type: ReportType.Unknown,
  },
  {
    id: 'zahtev-titar-paster',
    name: 'Titer Paster request',
    category: 'ExternalDocuments',
    // Downloadable Serbian-only Titar Paster request PDF
    type: ReportType.Unknown,
  },
  {
    id: 'pregled-jetre',
    name: 'Liver Examination',
    category: 'ExternalDocuments',
    inputs: [
      { formControlName: 'ks', type: 'textbox', hint: '(CBC;PCV,TOT WBC)' },
      {
        formControlName: 'biohemija',
        type: 'textbox',
        hint: '(AP;AST;ALT;TOT BIL;TP;CREATININ ;UREA; )',
      },
      { formControlName: 'se', type: 'textbox' },
      {
        formControlName: 'urin',
        type: 'textbox',
        hint: '(SG;PH;BILIRUBIN 4x )',
      },
      {
        formControlName: 'ultrazvukNalaz',
        type: 'textbox',
        hint: 'Veličina jetre; uski grudni koš (cm) 6,93+0,14 x t.m.(kg) | širok grudni koš (cm) 5,64+0,15 x t.m.(kg) | Struktura jetre; normalna zrnasta, promenjena',
      },
      { formControlName: 'rentgenNalaz', type: 'textbox' },
      {
        formControlName: 'biopsija',
        type: 'textbox',
        hint: '(laparaskop, elektroskalpel)',
      },
      { formControlName: 'dijagnostickalaparatomija', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.LiverExamination,
  },
  {
    id: 'potvrde-antibiogram',
    name: 'Antibiogram Certificate',
    category: 'Laboratories',
    isBigHeight: true,
    // the UI is provided by a custom component
    type: ReportType.AntibiogramCertificate,
  },

  {
    id: 'lab-biohemija-results',
    name: 'Results of Biochemical Blood Analysis (Dog)',
    category: 'Laboratories',
    isBigHeight: true,
    // UI provided by custom component `LabBiohemijaDogComponent`
    type: ReportType.LabBiochemDog,
  },
  {
    id: 'lab-biohemija-results-cat',
    name: 'Results of Biochemical Blood Analysis (Cat)',
    category: 'Laboratories',
    isBigHeight: true,
    // UI provided by custom component `LabBiohemijaCatComponent`
    type: ReportType.LabBiochemCat,
  },
  {
    id: 'lab-hemato-bio-dog',
    name: 'Results Hematology and Biochemical Analysis (Dog)',
    category: 'Laboratories',
    isBigHeight: true,
    type: ReportType.LabHematoBioDog,
  },
  {
    id: 'lab-hemato-dog',
    name: 'Results Hematology Analysis (Dog)',
    category: 'Laboratories',
    isBigHeight: true,
    type: ReportType.LabHematoDog,
  },
  {
    id: 'lab-hemato-cat',
    name: 'Results Hematology Analysis (Cat)',
    category: 'Laboratories',
    isBigHeight: true,
    type: ReportType.LabHematoCat,
  },
  {
    id: 'lab-hemato-bio-cat',
    name: 'Results Hematology and Biochemical Analysis (Cat)',
    category: 'Laboratories',
    isBigHeight: true,
    type: ReportType.LabHematoBioCat,
  },
  {
    id: 'cytological-skin-impression',
    name: 'Results of Cytological Examination of Skin Impression',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'placeOfIntake', type: 'input' },
      { formControlName: 'typeOfColoring', type: 'input' },
      { formControlName: 'typeOfReview', type: 'input' },
      { formControlName: 'finding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfCytologicalExaminationOfTheSkinImpression,
  },

  {
    id: 'clinical-examination-report',
    name: 'Clinical Examination Report',
    category: 'Certificates',
    type: ReportType.ClinicalExaminationReport,
  },
  {
    id: 'postoperativna-nega',
    name: 'Postoperative Care',
    category: 'Instructions',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.PostoperativeCare,
  },
  {
    id: 'upustvo-nege-posle-carskog-reza',
    name: 'Post-Cesarean Care Instructions',
    category: 'Instructions',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.PostCesareanCare,
  },
  {
    id: 'uputstvo-za-putovanje',
    name: 'Travel Instructions',
    category: 'Instructions',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.TravelInstructions,
  },
  {
    id: 'bandages-maintenance',
    name: 'Bandages and Dressings — Maintenance Procedure',
    category: 'Instructions',
    isBigHeight: true,
    // the UI is provided by a custom component (BandagesMaintenanceComponent)
    type: ReportType.BandagesMaintenance,
  },
  {
    id: 'durogesic-patch-instructions',
    name: 'Durogesic Patch Instructions',
    category: 'Instructions',
    isBigHeight: false,
    // the UI is provided by a custom component (DurogesicPatchInstructionsComponent)
    type: ReportType.DurogesicPatchInstructions,
  },
  {
    id: 'instrukcije-elizabetina-kragna',
    name: 'Elizabethan Collar Instructions',
    category: 'Instructions',
    isBigHeight: true,
    inputs: [
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.EcollarInstructions,
  },
  {
    id: 'clinical-examination-certificate',
    name: 'Clinical Examination',
    category: 'Certificates',
    isBigHeight: true,
    // the UI is provided by a custom component (ClinicalExaminationCertificateComponent)
    type: ReportType.ClinicalExamination,
  },
  {
    id: 'discharge-epicrisis',
    name: 'Discharge with Epicrisis',
    category: 'Certificates',
    isBigHeight: true,
    // the UI is provided by a custom component (DischargeEpicrisisComponent)
    type: ReportType.DischargeEpicrisis,
  },
  {
    id: 'certificate-origin-health',
    name: 'Certificate of Origin and Health',
    category: 'Certificates',
    isBigHeight: true,
    // the UI is provided by a custom component (CertificateOriginHealthComponent)
    type: ReportType.CertificateOriginHealth,
  },
  {
    id: 'rabies-examination-certificate',
    name: 'Rabies Examination Certificate',
    category: 'Certificates',
    isBigHeight: true,
    // the UI is provided by a custom component (RabiesExaminationCertificateComponent)
    type: ReportType.RabiesExaminationCertificate,
  },
  {
    id: 'qualitative-urine-sediment',
    name: 'Results of Qualitative Urine and Sediment Examination',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'leukocytes', type: 'input' },
      { formControlName: 'nitrites', type: 'input' },
      { formControlName: 'urobilinogen', type: 'input' },
      { formControlName: 'pH', type: 'input' },
      { formControlName: 'proteins', type: 'input' },
      { formControlName: 'hemoglobin', type: 'input' },
      { formControlName: 'specificGravity', type: 'input' },
      { formControlName: 'ketones', type: 'input' },
      { formControlName: 'bilirubin', type: 'input' },
      { formControlName: 'glucose', type: 'input' },
      { formControlName: 'ascorbicAcid', type: 'input' },
      { formControlName: 'erythrocytes', type: 'input' },
      { formControlName: 'bacteria', type: 'input' },
      { formControlName: 'mucus', type: 'input' },
      { formControlName: 'salts', type: 'input' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfQualitativeUrineAndSedimentExamination,
  },
  {
    id: 'test-results',
    name: 'Test Results',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'beapTest', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.TestResults,
  },

  {
    id: 'potvrda-sterilizacije',
    name: 'Sterilization Certificate',
    category: 'Certificates',
    inputs: [
      { formControlName: 'dateOfSterilization', type: 'date' },
      { formControlName: 'protocolNumber', type: 'input' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.SterilizationCertificate,
  },
  {
    id: 'potvrda-blanko',
    name: 'Blank Certificate',
    category: 'Certificates',
    isBigHeight: true,
    // the UI is provided by a custom component (PotvrdaBlankoComponent)
    type: ReportType.BlankCertificate,
  },
  {
    id: 'rabies-vaccination-certificate',
    name: 'Rabies Vaccination Certificate for Dogs and Cats',
    category: 'Certificates',
    type: ReportType.RabiesVaccinationCertificate,
    isBigHeight: true,
  },
  // Serbian-only form entry removed; use the existing English "Sterilization Certificate" entry above.
  {
    id: 'owner-statement-euthanasia',
    name: "Owner's Request for Animal Euthanasia",
    category: 'Consent',
    type: ReportType.OwnerStatementEuthanasia,
  },
  {
    id: 'cytological-ear-swab',
    name: 'Results of Cytological Examination of Ear Swab',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'placeOfIntake', type: 'input' },
      { formControlName: 'typeOfColoring', type: 'input' },
      { formControlName: 'typeOfReview', type: 'input' },
      { formControlName: 'finding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfCytologicalExaminationOfEarSwab,
  },
  {
    id: 'cytological-skin-swab',
    name: 'Results of Cytological Examination of Skin Swab',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'placeOfIntake', type: 'input' },
      { formControlName: 'typeOfColoring', type: 'input' },
      { formControlName: 'typeOfReview', type: 'input' },
      { formControlName: 'finding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfCytologicalExaminationOfSkinSwab,
  },
  {
    id: 'cytological-cellophane-tape',
    name: 'Results of Cytological Examination of Cellophane Tape Skin Swab',
    category: 'Laboratories',
    inputs: [
      { formControlName: 'placeOfIntake', type: 'input' },
      { formControlName: 'typeOfColoring', type: 'input' },
      { formControlName: 'typeOfReview', type: 'input' },
      { formControlName: 'finding', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfCytologicalExaminationOfCellophaneTapeSkinSwab,
  },
  {
    id: 'clinic-observation-dog-rabies',
    name: 'Clinical Observation of Dog for Rabies',
    category: 'Findings',
    inputs: [
      { formControlName: 'observations', type: 'input' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ClinicalObservationOfDogForRabies,
  },
  {
    id: 'procedure-under-general-anesthesia',
    name: "Owner's Consent for Surgical / Diagnostic Procedure under General Anesthesia",
    category: 'Consent',
    inputs: [
      { formControlName: 'recommendedHematology', type: 'input' },
      { formControlName: 'recommendedEkg', type: 'input' },
      { formControlName: 'recommendedBiopsy', type: 'input' },
      { formControlName: 'recommendedBiochemistry', type: 'input' },
      { formControlName: 'recommendedEho', type: 'input' },
      { formControlName: 'recommendedEndoscopy', type: 'input' },
      { formControlName: 'recommendedRtg', type: 'input' },
      { formControlName: 'recommendedMri', type: 'input' },
      { formControlName: 'recommendedUrineAnalysis', type: 'input' },
      { formControlName: 'recommendedOther', type: 'textbox' },
      { formControlName: 'performedHematology', type: 'input' },
      { formControlName: 'performedEkg', type: 'input' },
      { formControlName: 'performedBiopsy', type: 'input' },
      { formControlName: 'performedBiochemistry', type: 'input' },
      { formControlName: 'performedEho', type: 'input' },
      { formControlName: 'performedEndoscopy', type: 'input' },
      { formControlName: 'performedRtg', type: 'input' },
      { formControlName: 'performedMri', type: 'input' },
      { formControlName: 'performedUrineAnalysis', type: 'input' },
      { formControlName: 'performedOther', type: 'textbox' },
      { formControlName: 'interventionProcedure', type: 'textbox' },
      { formControlName: 'notesInstructions', type: 'textbox' },
      {
        formControlName: 'costEstimationMentionedProceduresFrom',
        type: 'input',
      },
      { formControlName: 'costEstimationMentionedProceduresTo', type: 'input' },
      { formControlName: 'additionalCostFrom', type: 'input' },
      { formControlName: 'additionalCostTo', type: 'input' },
      { formControlName: 'date', type: 'date' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.OwnersConsentForSurgical,
  },
  {
    id: 'test-result-4d',
    name: '4D Test Result',
    category: 'Findings',
    inputs: [
      { formControlName: 'heartworm', type: 'textbox' },
      { formControlName: 'anaplasmaPhagocytophilum', type: 'textbox' },
      { formControlName: 'ehrlichiaCanis', type: 'textbox' },
      { formControlName: 'lymeDisease', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.TestResult4D,
  },
  {
    id: 'results-chw-testing',
    name: 'Results of CHW (Canine Heartworm) Testing',
    category: 'Findings',
    inputs: [
      { formControlName: 'testPresenceDirofilariaImmitis', type: 'textbox' },
      { formControlName: 'note', type: 'textbox' },
      { formControlName: 'veterinarianName', type: 'input', require: true },
    ],
    type: ReportType.ResultsOfCHWTesting,
  },
  {
    id: 'karton-tretmana-dirofilarioze',
    name: 'Cardio-Pulmonary Dirofilariasis (Heartworm Disease) Treatment Record',
    category: 'Protocols',
    isBigHeight: true,
    // the UI is provided by a custom component (CardioPulmonaryDirofilariasisComponent)
    type: ReportType.HeartwormDisease,
  },
  {
    id: 'prot-24h-protokol',
    name: '24-hour Protocol',
    category: 'Protocols',
    // Downloadable PDF available in assets/document/Prot-24h-protokol.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'prot-hemoterapije',
    name: 'Chemotherapy Protocol',
    category: 'Protocols',
    isBigHeight: true,
    // the UI is provided by a custom component (ChemotherapyProtocolComponent)
    type: ReportType.ChemotherapyProtocol,
  },
  {
    id: 'prot-visednevni',
    name: 'Multi-day Protocol',
    category: 'Protocols',
    // Downloadable PDF available in assets/document/Prot-Visednevni.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'prot-anestezije',
    name: 'Anesthesia Protocol',
    category: 'Protocols',
    // Downloadable PDF available in assets/document/Protokol-Anestezije.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'prot-dirofilarioze',
    name: 'Cardio-Pulmonary Dirofilariasis — Treatment Record (Protocol)',
    category: 'Protocols',
    // Downloadable PDF available in assets/document/Protokol-Dirofilarioze.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'prot-infuzije',
    name: 'Infusion Protocol',
    category: 'Protocols',
    // Downloadable PDF available in assets/document/Protokol-infuzuje.pdf
    type: ReportType.Unknown,
  },
  {
    id: 'anamnesticki-karton',
    name: 'Anamnestic Card',
    category: 'Protocols',
    isBigHeight: true,
    groups: [
      {
        groupName: 'generalInfo',
        inputs: [
          { formControlName: 'mainReason', type: 'textbox' },
          { formControlName: 'historyPresentIllness', type: 'textbox' },
          { formControlName: 'vaccines', type: 'textbox' },
        ],
      },
      {
        groupName: 'parasites',
        inputs: [
          { formControlName: 'antiparasiticEndo_howOften', type: 'input' },
          { formControlName: 'antiparasiticEndo_lastTime', type: 'input' },
          { formControlName: 'antiparasiticEndo_product', type: 'input' },
          { formControlName: 'antiparasiticEcto_howOften', type: 'input' },
          { formControlName: 'antiparasiticEcto_lastTime', type: 'input' },
          { formControlName: 'antiparasiticEcto_product', type: 'input' },
        ],
      },
      {
        groupName: 'pastMedical',
        inputs: [
          { formControlName: 'pastIllnesses', type: 'textbox' },
          { formControlName: 'surgicalInterventions', type: 'textbox' },
          { formControlName: 'injuries', type: 'textbox' },
          { formControlName: 'currentMedications', type: 'textbox' },
          { formControlName: 'housing', type: 'input' },
          { formControlName: 'allergies', type: 'textbox' },
          { formControlName: 'nutrition', type: 'select' },
        ],
      },
      {
        groupName: 'additionalAnamnestic',
        inputs: [{ formControlName: 'additionalData', type: 'textbox' }],
      },
      {
        groupName: 'clinicalExam',
        inputs: [
          { formControlName: 'bodyWeightKg', type: 'input' },
          { formControlName: 'temperatureC', type: 'input' },
          { formControlName: 'dehydrationPercent', type: 'input' },
          { formControlName: 'pulse', type: 'input' },
          { formControlName: 'respiration', type: 'input' },
          { formControlName: 'vpk', type: 'input' },
          { formControlName: 'arterijskiPritisak', type: 'input' },
          { formControlName: 'mucousMembranes', type: 'textbox' },
          { formControlName: 'lymphNodes', type: 'textbox' },
          { formControlName: 'clinicalChanges', type: 'textbox' },
          { formControlName: 'diffDiagnosis1', type: 'input' },
          { formControlName: 'diffDiagnosis2', type: 'input' },
          { formControlName: 'diffDiagnosis3', type: 'input' },
          { formControlName: 'diffDiagnosis4', type: 'input' },
          { formControlName: 'diffDiagnosis5', type: 'input' },
          { formControlName: 'diffDiagnosis6', type: 'input' },
        ],
      },
      {
        groupName: 'workListAndNotes',
        inputs: [
          { formControlName: 'workList', type: 'textbox' },
          { formControlName: 'comments', type: 'textbox' },
          { formControlName: 'diagnosis', type: 'textbox' },
          { formControlName: 'therapy', type: 'textbox' },
          { formControlName: 'control', type: 'textbox' },
        ],
      },
      {
        groupName: 'veterinarianInfo',
        inputs: [
          { formControlName: 'veterinarianName', type: 'input', require: true },
        ],
      },
    ],
    type: ReportType.AnamnesticCard,
  },
  {
    id: 'fna-finding',
    name: 'FNA Finding',
    category: 'Findings',
    type: ReportType.FnaFinding,
  },
  {
    id: 'fip-test-result',
    name: 'Test for presence of FIP (Ag)',
    category: 'Findings',
    type: ReportType.FipTest,
  },
  {
    id: 'fiv-test-result',
    name: 'Test for presence of FIV (Ag)',
    category: 'Findings',
    type: ReportType.FivTest,
  },
];
